import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Lock, LogOut, Unlock } from 'lucide-react'

import type { Customer } from '@/lib/api/customer-api'
import type {
    ShopAccountAddress,
    ShopAccountAudit,
    ShopAccountDetail,
} from '@/lib/api/shop-account-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { CustomerSearchSelect } from '@/components/forms/customer-search-select'
import { providerLabel } from '@/components/retail/shop-account-labels'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useProvinces, useWards } from '@/hooks/use-provinces-query'
import {
    useShopAccount,
    useShopAccountAction,
    useShopAccountOrders,
} from '@/hooks/use-shop-account-query'
import { OrderStatusBadge } from '@/components/sections/order-status-badge'
import { formatDateTime, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { RETAIL_MEMBER_TIERS_ENABLED } from '@/lib/retail-features'

// Bán lẻ › Tài khoản khách — chi tiết (wireframe ErpKhachBanLeChiTiet). Hạng
// và chi tiêu đọc trực tiếp từ đơn đã giao (hợp đồng §14.3.3.4); đơn gần đây
// lọc theo tài khoản, không theo khách ERP hay SĐT (§14.4.1).
export const Route = createFileRoute(
    '/_app/_authed/_sidebar/retail/accounts/$accountId',
)({
    component: AccountDetailPage,
})

const AUDIT_LABEL: Record<ShopAccountAudit['action'], () => string> = {
    lock: () => m.shop_account_audit_lock(),
    unlock: () => m.shop_account_audit_unlock(),
    revoke_sessions: () => m.shop_account_audit_revoke(),
    customer_link: () => m.shop_account_audit_link(),
    merge: () => m.shop_account_audit_merge(),
}

function AccountDetailPage() {
    const { accountId } = Route.useParams()
    const query = useShopAccount(accountId)
    const d = query.data?.data
    if (query.isLoading) {
        return (
            <p className="page-pad text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    }
    if (!d) {
        return (
            <p className="page-pad text-destructive">
                {m.error_shop_account_not_found()}
            </p>
        )
    }
    return <AccountDetail detail={d} />
}

type Confirm = 'lock' | 'unlock' | 'revoke' | null

export function AccountDetail({ detail: d }: { detail: ShopAccountDetail }) {
    const { hasPermission } = useAuth()
    const canLock = hasPermission('shop_accounts.lock')
    const canLink = hasPermission('shop_accounts.link')
    const a = d.account
    const action = useShopAccountAction(a.id)
    const [confirm, setConfirm] = useState<Confirm>(null)
    const [linking, setLinking] = useState(false)
    const [picked, setPicked] = useState<Customer | null>(null)
    const locked = a.status === 'locked'

    const run = (act: 'lock' | 'unlock' | 'revoke') =>
        action.mutate({ action: act }, { onSettled: () => setConfirm(null) })
    const link = (customerId: string | null) =>
        action.mutate(
            { action: 'link', customerId },
            { onSuccess: () => setLinking(false) },
        )

    return (
        <div className="space-y-4 page-pad">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-2">
                    <PageBackButton className="-my-1" />
                    <div className="min-w-0">
                        <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
                            <span className="truncate">{a.name}</span>
                            {locked && (
                                <Badge variant="destructive">
                                    {m.shop_account_status_locked()}
                                </Badge>
                            )}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {m.shop_account_created({
                                at: formatDateTime(a.created_at),
                                source:
                                    a.source === 'web'
                                        ? m.shop_account_source_web()
                                        : m.shop_account_source_miniapp(),
                            })}
                        </p>
                    </div>
                </div>
                {canLock && (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirm('revoke')}
                        >
                            <LogOut className="mr-1 size-4" />
                            {m.shop_account_revoke()}
                        </Button>
                        {locked ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setConfirm('unlock')}
                            >
                                <Unlock className="mr-1 size-4" />
                                {m.shop_account_unlock()}
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setConfirm('lock')}
                            >
                                <Lock className="mr-1 size-4" />
                                {m.shop_account_lock()}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="min-w-0 space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                {m.shop_account_identities()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.shop_account_identity_method()}
                                            </TableHead>
                                            <TableHead>
                                                {m.shop_account_identity_value()}
                                            </TableHead>
                                            <TableHead>
                                                {m.shop_account_identity_verified()}
                                            </TableHead>
                                            <TableHead>
                                                {m.shop_account_identity_last()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {d.identities.map((i) => (
                                            <TableRow
                                                key={`${i.provider}:${i.subject}`}
                                            >
                                                <TableCell>
                                                    {providerLabel(i.provider)}
                                                </TableCell>
                                                <TableCell className="break-all">
                                                    {i.subject}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDateTime(
                                                        i.verified_at,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {i.last_login_at
                                                        ? formatDateTime(
                                                              i.last_login_at,
                                                          )
                                                        : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            {d.same_phone_accounts.length > 0 && (
                                <div className="rounded-md bg-muted/50 p-3 text-sm">
                                    <p>{m.shop_account_same_phone()}</p>
                                    <ul className="mt-1 space-y-1">
                                        {d.same_phone_accounts.map((o) => (
                                            <li key={o.id}>
                                                <Link
                                                    to="/retail/accounts/$accountId"
                                                    params={{ accountId: o.id }}
                                                    className="text-primary hover:underline"
                                                >
                                                    {o.name}
                                                </Link>{' '}
                                                <span className="text-muted-foreground">
                                                    ({o.email ?? o.phone})
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                {m.shop_account_contact()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-[160px_1fr]">
                                <dt className="text-muted-foreground">
                                    {m.shop_account_col_name()}
                                </dt>
                                <dd>{a.name}</dd>
                                <dt className="text-muted-foreground">Email</dt>
                                <dd className="break-all">{a.email ?? '—'}</dd>
                                <dt className="text-muted-foreground">
                                    {m.shop_account_col_phone()}
                                </dt>
                                <dd>
                                    {a.phone ?? '—'}
                                    {a.phone && (
                                        <span className="ml-2 text-muted-foreground">
                                            {a.phone_verified_at
                                                ? m.shop_account_phone_verified()
                                                : m.shop_account_phone_unverified()}
                                        </span>
                                    )}
                                </dd>
                                <dt className="text-muted-foreground">
                                    {m.shop_account_col_link()}
                                </dt>
                                <dd className="flex flex-wrap items-center gap-2">
                                    {a.customer_id ? (
                                        <Link
                                            to="/customers/$customerId"
                                            search={{ tab: 'info' }}
                                            params={{
                                                customerId: a.customer_id,
                                            }}
                                            className="text-primary hover:underline"
                                        >
                                            {a.customer_code} ·{' '}
                                            {a.customer_name}
                                        </Link>
                                    ) : (
                                        <span className="text-muted-foreground">
                                            {m.shop_account_not_linked()}
                                        </span>
                                    )}
                                    {canLink && (
                                        <>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    setPicked(null)
                                                    setLinking(true)
                                                }}
                                            >
                                                {m.shop_account_link_change()}
                                            </Button>
                                            {a.customer_id && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    disabled={action.isPending}
                                                    onClick={() => link(null)}
                                                >
                                                    {m.shop_account_link_remove()}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </dd>
                            </dl>
                            <p className="mt-3 text-xs text-muted-foreground">
                                {m.shop_account_link_note()}
                            </p>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                {m.shop_account_addresses()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {d.addresses.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    {m.shop_account_addresses_empty()}
                                </p>
                            ) : (
                                <div className="grid gap-3 sm:grid-cols-2">
                                    {d.addresses.map((ad) => (
                                        <AddressCard key={ad.id} a={ad} />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                {m.shop_account_recent_orders()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <RecentOrders accountId={a.id} />
                        </CardContent>
                    </Card>
                </div>

                <div className="min-w-0 space-y-4">
                    {/* Hạng thành viên đang ẩn (ERP-07). */}
                    {RETAIL_MEMBER_TIERS_ENABLED && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    {m.shop_account_tier()}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 text-sm">
                                <p className="text-2xl font-semibold">
                                    {d.tier.current?.name ?? '—'}
                                </p>
                                <p className="tabular-nums">
                                    {m.shop_account_col_spend()}:{' '}
                                    {formatVnd(d.tier.spend)}
                                </p>
                                {d.tier.next && d.tier.remaining !== null && (
                                    <p className="text-muted-foreground">
                                        {m.shop_account_tier_remaining({
                                            amount: formatVnd(d.tier.remaining),
                                            tier: d.tier.next.name,
                                        })}
                                    </p>
                                )}
                                <p className="text-muted-foreground">
                                    {m.shop_account_tier_note()}
                                </p>
                            </CardContent>
                        </Card>
                    )}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                {m.shop_account_summary()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <dl className="grid grid-cols-[1fr_auto] gap-y-2 text-sm">
                                {!RETAIL_MEMBER_TIERS_ENABLED && (
                                    <>
                                        <dt className="text-muted-foreground">
                                            {m.shop_account_col_spend()}
                                        </dt>
                                        <dd className="tabular-nums">
                                            {formatVnd(d.tier.spend)}
                                        </dd>
                                    </>
                                )}
                                <dt className="text-muted-foreground">
                                    {m.shop_account_col_orders()}
                                </dt>
                                <dd className="tabular-nums">
                                    {d.order_stats.orders}
                                </dd>
                                <dt className="text-muted-foreground">
                                    {m.shop_account_first_order()}
                                </dt>
                                <dd>
                                    {d.order_stats.first_order_at
                                        ? formatDateTime(
                                              d.order_stats.first_order_at,
                                          )
                                        : '—'}
                                </dd>
                                <dt className="text-muted-foreground">
                                    {m.shop_account_last_login()}
                                </dt>
                                <dd>
                                    {a.last_login_at
                                        ? formatDateTime(a.last_login_at)
                                        : '—'}
                                </dd>
                            </dl>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                {m.shop_account_audit()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {d.audit.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    {m.shop_account_audit_empty()}
                                </p>
                            ) : (
                                <ul className="space-y-2 text-sm">
                                    {d.audit.map((x) => (
                                        <li key={x.id}>
                                            <span className="font-medium">
                                                {AUDIT_LABEL[x.action]()}
                                            </span>{' '}
                                            <span className="text-muted-foreground">
                                                {x.actor_name ?? '—'} ·{' '}
                                                {formatDateTime(x.created_at)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            <Dialog
                open={confirm !== null}
                onOpenChange={(o) => !o && setConfirm(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {confirm === 'lock'
                                ? m.shop_account_lock_confirm({ name: a.name })
                                : confirm === 'unlock'
                                  ? m.shop_account_unlock_confirm({
                                        name: a.name,
                                    })
                                  : m.shop_account_revoke_confirm({
                                        name: a.name,
                                    })}
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        {confirm === 'lock'
                            ? m.shop_account_lock_note()
                            : confirm === 'revoke'
                              ? m.shop_account_revoke_note()
                              : m.shop_account_unlock_note()}
                    </p>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setConfirm(null)}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            variant={
                                confirm === 'lock' ? 'destructive' : 'default'
                            }
                            disabled={action.isPending}
                            onClick={() => confirm && run(confirm)}
                        >
                            {m.common_confirm()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={linking} onOpenChange={setLinking}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.shop_account_link_change()}
                        </DialogTitle>
                    </DialogHeader>
                    <CustomerSearchSelect value={picked} onChange={setPicked} />
                    <p className="text-xs text-muted-foreground">
                        {m.shop_account_link_note()}
                    </p>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setLinking(false)}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            disabled={action.isPending}
                            onClick={() => link(picked?.id ?? null)}
                        >
                            {m.common_save()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

function AddressCard({ a }: { a: ShopAccountAddress }) {
    const provinces = useProvinces().data ?? []
    const wards = useWards(a.province_code).data ?? []
    const province =
        provinces.find((p) => p.code === a.province_code)?.name ??
        a.province_code
    const ward = wards.find((w) => w.code === a.ward_code)?.name ?? a.ward_code
    return (
        <div className="space-y-1 rounded-md border p-3 text-sm">
            <p className="flex items-center gap-2 font-medium">
                {a.recipient_name}
                {a.is_default && (
                    <Badge variant="secondary">
                        {m.shop_account_address_default()}
                    </Badge>
                )}
            </p>
            <p className="tabular-nums">{a.phone}</p>
            <p className="text-muted-foreground">
                {a.address_line}, {ward}, {province}
            </p>
        </div>
    )
}

/** 10 đơn mới nhất của tài khoản; mã đơn mở danh sách đơn đã lọc theo mã. */
function RecentOrders({ accountId }: { accountId: string }) {
    const q = useShopAccountOrders(accountId)
    const rows = q.data?.data ?? []
    if (q.isLoading) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    }
    if (q.isError) {
        return (
            <p className="text-sm text-destructive">
                {m.shop_account_orders_failed()}
            </p>
        )
    }
    if (rows.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.shop_account_orders_empty()}
            </p>
        )
    }
    return (
        <div className="overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.shop_account_order_code()}</TableHead>
                        <TableHead>{m.shop_account_order_date()}</TableHead>
                        <TableHead>{m.shop_account_order_status()}</TableHead>
                        <TableHead className="text-right">
                            {m.shop_account_order_total()}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((o) => (
                        <TableRow key={o.order_code}>
                            <TableCell>
                                <Link
                                    to="/orders"
                                    search={{ q: o.order_code, period: 'all' }}
                                    className="font-medium text-primary hover:underline"
                                >
                                    {o.order_code}
                                </Link>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                                {formatDateTime(o.created_at)}
                            </TableCell>
                            <TableCell>
                                <OrderStatusBadge status={o.status} />
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {formatVnd(o.total_amount)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    )
}
